import { AlertTriangle, Check, Eye, Pencil } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthShell } from '@/components/AuthShell'
import { Alert, Button } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { canWrite, denialUrl, grantConsent, readConsentRequest } from '@/lib/mcp'

/**
 * The OAuth consent screen.
 *
 * The MCP service validates an authorization request and then redirects the
 * browser here with the parameters as query string. This page's only job is to
 * tell the user plainly who is asking and what they would be able to do, and to
 * turn a click into a POST to /oauth/consent.
 *
 * It is deliberately dull. A consent screen that hurries the user, or that
 * makes Grant obviously the intended button, is how people end up connecting
 * things they did not mean to.
 */
export function Authorize() {
  const [params] = useSearchParams()
  const { isLoggedIn, user } = useAuth()
  const request = readConsentRequest(params)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // A malformed request never reaches a consent screen — the MCP service
  // rejects it — so this means the user arrived here by some other route.
  if (!request) {
    return (
      <Shell title="Nothing to authorize">
        <Alert>
          This page is opened by an app asking for access to your reminders, and it needs
          parameters this visit does not have. Start again from the app.
        </Alert>
      </Shell>
    )
  }

  if (!isLoggedIn) {
    // The MCP service will not issue anything without a valid session token, so
    // this is a convenience rather than the check that matters. The full URL is
    // preserved so signing in returns here with the request intact.
    const returnTo = `/authorize?${params.toString()}`
    return (
      <Shell title="Sign in to continue">
        <p className="text-sm text-ink-muted">
          <strong className="font-medium text-ink">{request.client_name}</strong> wants access to
          your reminders. Sign in first, so you can see what you are approving.
        </p>
        <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="block">
          <Button className="w-full">Sign in</Button>
        </Link>
      </Shell>
    )
  }

  function approve() {
    setBusy(true)
    setError('')
    grantConsent(request!)
      .then((target) => {
        // A full navigation, not a router push: the target is the client's own
        // callback — a loopback server or a custom scheme — not a route here.
        window.location.href = target
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not complete authorization')
        setBusy(false)
      })
  }

  function decline() {
    window.location.href = denialUrl(request!)
  }

  const writes = canWrite(request.scope)

  return (
    <Shell title={`${request.client_name} wants access`}>
      <div className="space-y-5">
        {error && <Alert>{error}</Alert>}

        <p className="text-sm text-ink-muted">
          Connecting as <strong className="font-medium text-ink">{user?.email}</strong>. If you
          grant this, the app will be able to:
        </p>

        <ul className="space-y-2.5">
          <Permission icon={<Eye className="size-4" aria-hidden="true" />}>
            <strong className="font-medium text-ink">Read your records</strong> — names, providers,
            dates, costs and notes.
          </Permission>
          {writes && (
            <Permission icon={<Pencil className="size-4" aria-hidden="true" />}>
              <strong className="font-medium text-ink">Add and change records</strong> — create new
              ones, edit them, and archive them.
            </Permission>
          )}
        </ul>

        <div className="rounded-lg bg-surface-muted px-3 py-2.5 text-xs text-ink-muted">
          <p>
            It cannot delete anything, change your account settings, or read your password.
            {writes && ' Archiving a record here does not cancel anything with the provider.'}
          </p>
          <p className="mt-1.5">
            You can disconnect it at any time from{' '}
            <Link className="text-brand hover:underline" to="/settings">
              Settings
            </Link>
            , and it loses access immediately.
          </p>
        </div>

        {/* Only shown for a redirect that is not loopback: a remote callback
            means the code leaves this machine, which is worth naming. */}
        {!isLoopback(request.redirect_uri) && (
          <p className="flex items-start gap-2 text-xs text-ink-muted">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-soon" aria-hidden="true" />
            <span>
              You will be sent to <code className="break-all">{hostOf(request.redirect_uri)}</code>{' '}
              to finish. Only continue if you started this from that app.
            </span>
          </p>
        )}

        <div className="flex flex-col gap-2">
          <Button onClick={approve} loading={busy} className="w-full">
            <Check className="size-4" aria-hidden="true" />
            Grant access
          </Button>
          <Button variant="secondary" onClick={decline} disabled={busy} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </Shell>
  )
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <AuthShell title={title}>
      <div className="space-y-4">{children}</div>
    </AuthShell>
  )
}

function Permission({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-ink-muted">
      <span className="mt-0.5 shrink-0 text-brand">{icon}</span>
      <span>{children}</span>
    </li>
  )
}

function isLoopback(uri: string): boolean {
  const lowered = uri.toLowerCase()
  return (
    lowered.startsWith('http://127.0.0.1') ||
    lowered.startsWith('http://[::1]') ||
    lowered.startsWith('http://localhost')
  )
}

/** The host to show, or the raw URI if it is a custom scheme like claude://. */
function hostOf(uri: string): string {
  try {
    return new URL(uri).host || uri
  } catch {
    return uri
  }
}
